import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X, Wrench, PencilSimple } from "@phosphor-icons/react";
import UsersAdmin from "@/components/UsersAdmin";
import MaintenanceTab from "@/components/MaintenanceTab";

export default function Admin() {
  return (
    <AppLayout title="Admin & Settings" subtitle="User management, permissions and organization configuration">
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="rounded-sm bg-slate-100 mb-4">
          <TabsTrigger value="users" data-testid="tab-admin-users" className="rounded-sm">Users</TabsTrigger>
          <TabsTrigger value="positions" data-testid="tab-admin-positions" className="rounded-sm">Positions</TabsTrigger>
          <TabsTrigger value="certs" data-testid="tab-admin-certs" className="rounded-sm">Certifications</TabsTrigger>
          <TabsTrigger value="required-docs" data-testid="tab-admin-required-docs" className="rounded-sm">Required Certifications</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-admin-settings" className="rounded-sm">General</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-admin-maintenance" className="rounded-sm"><Wrench size={12} weight="bold" className="mr-1" />Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersAdmin /></TabsContent>
        <TabsContent value="positions"><PositionsTab /></TabsContent>
        <TabsContent value="certs"><CertsTab /></TabsContent>
        <TabsContent value="required-docs"><RequiredDocsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceTab /></TabsContent>
      </Tabs>
    </AppLayout>
  );
}

function ListEditor({ items, onAdd, onRemove, onRename, placeholder, testid }) {
  const [newItem, setNewItem] = useState("");
  const [editing, setEditing] = useState(null); // { original, draft }
  function add(e) {
    e.preventDefault();
    const v = newItem.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Already exists"); return; }
    onAdd(v); setNewItem("");
  }
  function startEdit(it) {
    setEditing({ original: it, draft: it });
  }
  function cancelEdit() { setEditing(null); }
  function commitEdit() {
    if (!editing) return;
    const v = (editing.draft || "").trim();
    if (!v) { toast.error("Name cannot be empty"); return; }
    if (v === editing.original) { setEditing(null); return; }
    if (items.includes(v)) { toast.error("Already exists"); return; }
    if (onRename) onRename(editing.original, v);
    setEditing(null);
  }
  return (
    <div>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <Input data-testid={`${testid}-input`} className="rounded-sm" placeholder={placeholder} value={newItem} onChange={(e) => setNewItem(e.target.value)} />
        <Button type="submit" data-testid={`${testid}-add`} className="rounded-sm bg-slate-900 text-white"><Plus size={14} weight="bold" className="mr-1" />Add</Button>
      </form>
      <ul className="space-y-1.5">
        {items.length === 0 ? <li className="text-sm text-slate-500">No entries yet.</li> :
          items.map((it) => (
            <li key={it} className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2 bg-white gap-2" data-testid={`${testid}-row-${it}`}>
              {editing && editing.original === it ? (
                <>
                  <Input
                    autoFocus
                    value={editing.draft}
                    onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitEdit(); } if (e.key === "Escape") cancelEdit(); }}
                    className="rounded-sm h-8 flex-1"
                    data-testid={`${testid}-edit-input-${it}`}
                  />
                  <Button size="sm" className="rounded-sm h-8 bg-slate-900 text-white" onClick={commitEdit} data-testid={`${testid}-edit-save-${it}`}>Save</Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={cancelEdit} data-testid={`${testid}-edit-cancel-${it}`}>Cancel</Button>
                </>
              ) : (
                <>
                  <span className="font-medium text-slate-900">{it}</span>
                  <div className="flex items-center gap-1">
                    {onRename && (
                      <Button size="icon" variant="ghost" className="text-slate-600 h-7 w-7" onClick={() => startEdit(it)} data-testid={`${testid}-edit-${it}`}><PencilSimple size={14} /></Button>
                    )}
                    <Button size="icon" variant="ghost" className="text-red-600 h-7 w-7" onClick={() => { if (window.confirm(`Remove "${it}"?`)) onRemove(it); }} data-testid={`${testid}-del-${it}`}><X size={14} /></Button>
                  </div>
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

function PositionsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/").then((r) => setSettings(r.data)); }, []);
  async function save(positions) {
    const { data } = await api.patch("/settings/", { positions });
    setSettings(data); toast.success("Positions updated");
  }
  if (!settings) return null;
  return (
    <div className="surface-grid p-5 max-w-2xl">
      <h3 className="font-display text-lg font-bold mb-1">Position Catalog</h3>
      <p className="text-xs text-slate-500 mb-4">Positions appear in the "Post Shift" form and as rows on the schedule grid.</p>
      <ListEditor items={settings.positions || []} placeholder='e.g. "Tour D — Weekend Day"' testid="pos"
        onAdd={(v) => save([...(settings.positions || []), v])}
        onRemove={(v) => save((settings.positions || []).filter((x) => x !== v))}
        onRename={(old, next) => save((settings.positions || []).map((x) => (x === old ? next : x)))} />
    </div>
  );
}

function CertsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/").then((r) => setSettings(r.data)); }, []);
  async function save(certifications_catalog) {
    const { data } = await api.patch("/settings/", { certifications_catalog });
    setSettings(data); toast.success("Certifications updated");
  }
  if (!settings) return null;
  return (
    <div className="surface-grid p-5 max-w-2xl">
      <h3 className="font-display text-lg font-bold mb-1">Certification Catalog</h3>
      <p className="text-xs text-slate-500 mb-4">Available credentials that can be required on shifts and assigned to members.</p>
      <ListEditor items={settings.certifications_catalog || []} placeholder='e.g. "AEMT"' testid="cert"
        onAdd={(v) => save([...(settings.certifications_catalog || []), v])}
        onRemove={(v) => save((settings.certifications_catalog || []).filter((x) => x !== v))}
        onRename={(old, next) => save((settings.certifications_catalog || []).map((x) => (x === old ? next : x)))} />
    </div>
  );
}

/**
 * RequiredDocsTab — Admin editor for the compliance matrix (which
 * documents are required for each certification classification).
 * Displays one card per classification (ALL, MVO/Driver, EMT,
 * Paramedic, plus anything the admin has added), each with an
 * add/remove list. A "Reset to defaults" action restores the
 * BSVAC-compiled defaults if the admin experiments and wants to undo.
 *
 * Add flow is centralized: one top-level "Add Certification/Document"
 * button opens a dialog with a Name field, a Required checkbox, and
 * Volunteer / MVO / EMT / Paramedic classification checkboxes (plus a
 * Select All). Adds the entry to every picked classification at once
 * and, when Required, notifies affected members.
 */
function RequiredDocsTab() {
  const [matrix, setMatrix] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    api.get("/settings/required-docs").then((r) => {
      setMatrix(r.data.matrix || {});
      setDefaults(r.data.defaults || {});
    });
  }, []);

  async function persist(next) {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/required-docs", { matrix: next });
      setMatrix(data.matrix);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  function removeDoc(cert, doc) {
    const cur = matrix[cert] || [];
    persist({ ...matrix, [cert]: cur.filter((d) => d !== doc) });
  }
  function removeCert(cert) {
    if (!window.confirm(`Remove classification "${cert}" and all its required documents from the matrix?`)) return;
    const { [cert]: _dropped, ...rest } = matrix;
    persist(rest);
  }
  async function resetDefaults() {
    if (!window.confirm("Restore the BSVAC default required-documents matrix? Any customisations will be lost.")) return;
    persist({ ...defaults });
    toast.success("Restored defaults");
  }

  if (!matrix) return null;
  const orderedCerts = ["ALL", ...Object.keys(matrix).filter((k) => k !== "ALL").sort()];

  return (
    <div className="space-y-5" data-testid="required-docs-tab">
      <div className="surface-grid p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="font-display text-lg font-bold">Required Certifications & Documents</h3>
            <p className="text-xs text-slate-500">
              For each classification, list the documents that BSVAC requires. Members who hold that classification
              must upload every document listed here for their compliance percent to reach 100%. Members without the
              classification see these as <em>optional</em> uploads.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              className="rounded-sm bg-slate-900 text-white"
              onClick={() => setAddOpen(true)}
              data-testid="required-docs-add-open"
            >
              <Plus size={14} weight="bold" className="mr-1" />Add Certification/Document
            </Button>
            <Button variant="outline" className="rounded-sm text-xs" onClick={resetDefaults} data-testid="required-docs-reset">
              Reset to defaults
            </Button>
          </div>
        </div>
      </div>

      {orderedCerts.map((cert) => (
        <CertificationBlock
          key={cert}
          cert={cert}
          docs={matrix[cert] || []}
          onRemoveDoc={(d) => removeDoc(cert, d)}
          onEditDoc={(d) => setEditTarget({ cert, name: d })}
          onRemoveCert={cert === "ALL" ? null : () => removeCert(cert)}
          saving={saving}
        />
      ))}

      <AddCertDocDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(nextMatrix, notified) => {
          setMatrix(nextMatrix);
          setAddOpen(false);
          const suffix = notified > 0 ? ` · ${notified} member${notified === 1 ? "" : "s"} notified` : "";
          toast.success(`Added${suffix}`);
        }}
      />
      <EditCertDocDialog
        target={editTarget}
        matrix={matrix}
        onClose={() => setEditTarget(null)}
        onSaved={(nextMatrix) => {
          setMatrix(nextMatrix);
          setEditTarget(null);
          toast.success("Updated");
        }}
      />
    </div>
  );
}

function EditCertDocDialog({ target, matrix, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState(false);
  const CLASSES = ["Volunteer", "MVO", "EMT", "EMT Intern", "Paramedic", "Paramedic Intern"];
  const CLASS_TO_KEY = { Volunteer: "ALL", MVO: "MVO", EMT: "EMT", "EMT Intern": "EMT Intern", Paramedic: "Paramedic", "Paramedic Intern": "Paramedic Intern" };

  useEffect(() => {
    if (!target) return;
    setName(target.name || "");
    // Pre-check every classification where this entry currently lives.
    const assigned = CLASSES.filter((c) => (matrix[CLASS_TO_KEY[c]] || []).includes(target.name));
    setClasses(assigned);
  }, [target, matrix]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter a name"); return; }
    if (classes.length === 0) { toast.error("Pick at least one classification"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/required-docs/edit", {
        old_name: target.name,
        new_name: name.trim(),
        classifications: classes,
      });
      onSaved(data.matrix);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  function toggle(c) {
    setClasses((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="required-docs-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Certification / Document</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1">
            <Label className="tab-tag">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="required-docs-edit-name" />
          </div>
          <div className="space-y-2">
            <Label className="tab-tag">Classifications</Label>
            <div className="grid grid-cols-2 gap-2">
              {CLASSES.map((c) => (
                <label key={c} className={`flex items-center gap-2 text-sm border rounded-sm px-2 py-2 cursor-pointer ${classes.includes(c) ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`} data-testid={`required-docs-edit-cls-${c}`}>
                  <input type="checkbox" checked={classes.includes(c)} onChange={() => toggle(c)} />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 text-white" data-testid="required-docs-edit-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddCertDocDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [required, setRequired] = useState(true);
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState(false);
  const CLASSES = ["Volunteer", "MVO", "EMT", "EMT Intern", "Paramedic", "Paramedic Intern"];

  useEffect(() => {
    if (open) { setName(""); setRequired(true); setClasses([]); }
  }, [open]);

  function toggle(c) {
    setClasses((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  }
  function toggleAll() {
    setClasses(classes.length === CLASSES.length ? [] : [...CLASSES]);
  }

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter a document name"); return; }
    if (classes.length === 0) { toast.error("Select at least one classification"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/required-docs/add", {
        name: name.trim(),
        required,
        classifications: classes,
      });
      onSaved(data.matrix, data.notified || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="required-docs-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Certification / Document</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Adds the entry to every picked classification. When marked Required, matching members are notified in their inbox.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1">
            <Label className="tab-tag">Certification / Document Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" placeholder='e.g. "NIMS 300 Certificate"' required data-testid="required-docs-add-name" />
          </div>
          <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2 py-2 bg-slate-50" data-testid="required-docs-add-required-wrap">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} data-testid="required-docs-add-required" />
            <span><strong>Required</strong> — counts toward paperwork compliance for the selected classifications</span>
          </label>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="tab-tag">Classifications</Label>
              <button type="button" className="text-[11px] underline text-blue-700" onClick={toggleAll} data-testid="required-docs-add-selectall">
                {classes.length === CLASSES.length ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CLASSES.map((c) => (
                <label key={c} className={`flex items-center gap-2 text-sm border rounded-sm px-2 py-2 cursor-pointer ${classes.includes(c) ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`} data-testid={`required-docs-add-cls-${c}`}>
                  <input type="checkbox" checked={classes.includes(c)} onChange={() => toggle(c)} />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 text-white" data-testid="required-docs-add-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CertificationBlock({ cert, docs, onRemoveDoc, onEditDoc, onRemoveCert, saving }) {
  const label = cert === "ALL" ? "All Members (universal)" : cert;
  return (
    <div className="surface-grid p-5" data-testid={`required-docs-block-${cert}`}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-display text-base font-bold text-slate-900">{label}</h4>
        {onRemoveCert && (
          <Button size="sm" variant="ghost" className="text-red-600 h-7 text-xs" onClick={onRemoveCert} data-testid={`required-docs-remove-cert-${cert}`}>
            <X size={12} weight="bold" className="mr-1" />Remove classification
          </Button>
        )}
      </div>
      <div className="tab-tag text-slate-500 mb-3">
        {docs.length} required document{docs.length === 1 ? "" : "s"}
      </div>
      {docs.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No required documents yet. Use "Add Certification/Document" at the top of the page.</p>
      ) : (
        <ul className="space-y-1.5">
          {docs.map((doc) => (
            <li key={doc} className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2 bg-white text-sm">
              <span>{doc}</span>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-slate-700 hover:text-slate-900 h-7 w-7"
                  onClick={() => onEditDoc && onEditDoc(doc)}
                  disabled={saving}
                  title="Edit"
                  data-testid={`required-docs-edit-${cert}-${doc}`}
                >
                  <PencilSimple size={14} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-red-600 h-7 w-7"
                  onClick={() => { if (window.confirm(`Remove "${doc}" from ${label}?`)) onRemoveDoc(doc); }}
                  disabled={saving}
                  data-testid={`required-docs-del-${cert}-${doc}`}
                >
                  <X size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [threshold, setThreshold] = useState(20);
  const [purchasing, setPurchasing] = useState("");
  const [org, setOrg] = useState("");
  const [memberSeq, setMemberSeq] = useState(null);
  const [nextId, setNextId] = useState("");
  useEffect(() => {
    api.get("/settings/").then((r) => { setSettings(r.data); setThreshold(r.data.monthly_hours_threshold); setPurchasing(r.data.purchasing_email || ""); setOrg(r.data.org_name || ""); });
    api.get("/settings/member-id-sequence").then((r) => { setMemberSeq(r.data); setNextId(String(r.data.next_member_id)); }).catch(() => {});
  }, []);
  async function save(e) {
    e.preventDefault();
    const { data } = await api.patch("/settings/", { monthly_hours_threshold: +threshold, purchasing_email: purchasing, org_name: org });
    setSettings(data); toast.success("Settings updated");
  }
  async function saveMemberSeq(e) {
    e.preventDefault();
    const n = parseInt(nextId, 10);
    if (!n || n < 1001) { toast.error("Next ID must be at least 1001"); return; }
    try {
      const { data } = await api.patch("/settings/member-id-sequence", { next_value: n });
      setMemberSeq(data);
      toast.success(`Next member ID set to ${data.next_member_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  }
  if (!settings) return null;
  return (
    <div className="space-y-4">
      <form onSubmit={save} className="surface-grid p-6 max-w-xl space-y-4">
        <div>
          <Label className="tab-tag">Monthly Hours Threshold</Label>
          <Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="rounded-sm" data-testid="settings-threshold" />
          <p className="text-xs text-slate-500 mt-1">Volunteers below this number of hours per month are flagged red on reports.</p>
        </div>
        <div>
          <Label className="tab-tag">Purchasing Email (default for approved supply requests)</Label>
          <Input value={purchasing} onChange={(e) => setPurchasing(e.target.value)} className="rounded-sm" data-testid="settings-purchasing" />
        </div>
        <div>
          <Label className="tab-tag">Organization Name</Label>
          <Input value={org} onChange={(e) => setOrg(e.target.value)} className="rounded-sm" data-testid="settings-org" />
        </div>
        <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="settings-save">Save settings</Button>
      </form>

      {memberSeq && (
        <form onSubmit={saveMemberSeq} className="surface-grid p-6 max-w-xl space-y-3">
          <div>
            <h3 className="font-display font-bold text-base">Member ID Sequence</h3>
            <p className="text-xs text-slate-500 mt-1">
              New members are assigned sequential IDs starting at <span className="mono">{memberSeq.base}</span>.
              The next new member will be assigned <span className="mono font-bold">#{memberSeq.next_member_id}</span>.
              Adjust the number below to skip ahead or renumber.
            </p>
          </div>
          <div>
            <Label className="tab-tag">Next Member ID</Label>
            <Input type="number" min={memberSeq.base} value={nextId} onChange={(e) => setNextId(e.target.value)} className="rounded-sm mono" data-testid="settings-next-member-id" />
          </div>
          <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="settings-save-next-member-id">Update sequence</Button>
        </form>
      )}
    </div>
  );
}
